import { createStyles } from "@mantine/core";

const useStyles = createStyles((theme) => ({
  content: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    marginBottom: "50px",
  },
  scheduleDetail: {
    display: "flex",
    gap: "20px",
    justifyContent: "space-between",
    [theme.fn.smallerThan("md")]: {
      flexDirection: "column",
      gap: "12px",
    },
  },
  wrapper: {
    width: "100%",
    overflowX: "auto",
  },
  mainContainer: {
    width: "100%",
  },

  // ── Toolbar bar (toggle + buttons) ──
  tabsAndBtnBar: {
    padding: "10px 11px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderRadius: 12,
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E5E5",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 8,
  },

  // Active/Inactive pill toggle
  activeInactiveParent: {
    display: "inline-flex",
    padding: 4,
    background: "#F4F4F5",
    border: "1px solid #E5E5E5",
    borderRadius: 10,
    gap: 0,
  },
  btnCommonCSS: {
    height: 36,
    padding: "0 18px",
    cursor: "pointer",
    color: "#737373",
    backgroundColor: "transparent",
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Inter",
    border: "none",
    borderRadius: 7,
    transition: "background 120ms ease, color 120ms ease",
  },
  activeBg: {
    backgroundColor: "#FFFFFF !important",
    color: "#171717 !important",
    boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
  },

  // ── Table container ──
  tableContainer: {
    backgroundColor: "#FFFFFF",
    border: "1px solid #E5E5E5",
    borderRadius: 12,
    overflow: "hidden",
  },
  scrollArea: {
    minHeight: "350px",
  },

  // ── Table head ──
  header: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    transition: "box-shadow 150ms ease",
    "& tr": {
      background: "#FAFAFA",
      borderBottom: "1px solid #E5E5E5",
    },
    "& th": {
      backgroundColor: "#FAFAFA !important",
      color: "#525252 !important",
      fontSize: "11px !important",
      fontWeight: "600 !important",
      fontFamily: "Inter !important",
      textTransform: "uppercase",
      letterSpacing: "0.4px",
      padding: "12px 14px",
      whiteSpace: "nowrap",
      borderBottom: "none",
    },
  },
  scrolled: {
    boxShadow: theme.shadows.sm,
  },

  // ── Table body ──
  table: {
    borderCollapse: "collapse",
    "& tbody tr": {
      borderBottom: "1px solid #F2F2F2",
      transition: "background 100ms ease",
      "&:last-child": {
        borderBottom: "none",
      },
    },
    "& tbody tr td": {
      fontSize: 13,
      fontWeight: 400,
      color: "#262626",
      fontFamily: "Inter",
      padding: "14px",
      verticalAlign: "middle",
    },
    "& tbody tr:hover td": {
      backgroundColor: "#FAFAFA",
    },
  },
  bg1: {
    "& td": { background: "#FFFFFF" },
  },
  bg2: {
    "& td": { background: "#FFFFFF" },
  },

  // Sticky first column
  stickyColumn: {
    position: "sticky",
    left: -1,
    zIndex: 2,
    backgroundColor: "#FFFFFF",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      right: "-7px",
      width: "7px",
      height: "100%",
      pointerEvents: "none",
      background: "linear-gradient(to right, rgba(0,0,0,0.06), transparent)",
    },
  },

  // ── Pill icon box ──
  pillIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  pillIconNarcotic: {
    background: "#FEE2E2",
    border: "1px solid #FECACA",
  },
  pillIconNormal: {
    background: "#FFF3DC",
    border: "1px solid #FFE4A8",
  },

  // ── Stock bar ──
  stockBarTrack: {
    height: 6,
    borderRadius: 999,
    background: "#F2F2F2",
    overflow: "hidden",
    maxWidth: 110,
    flex: 1,
  },
  stockBarFill: {
    height: "100%",
    borderRadius: 999,
    transition: "width 200ms ease",
  },

  // ── Status pills (days supply) ──
  statusPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 999,
    fontFamily: "Inter",
    whiteSpace: "nowrap",
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    flexShrink: 0,
  },

  // ── Chip (Narcotics / PRN / etc.) ──
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: "11px",
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 999,
    fontFamily: "Inter",
    whiteSpace: "nowrap",
    lineHeight: 1.4,
  },
  chipNarcotic: {
    background: "#FEE2E2",
    color: "#B91C1C",
    border: "1px solid #FECACA",
  },
  chipPRN: {
    background: "#F3E8FF",
    color: "#6B21A8",
    border: "1px solid #E9D5FF",
  },
  chipDiscontinued: {
    background: "#FEF3C7",
    color: "#92400E",
    border: "1px solid #FDE68A",
  },

  // ── Doctor's order button ──
  viewFileBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 30,
    padding: "0 10px",
    background: "#FFFFFF",
    color: "#171717",
    border: "1px solid #E5E5E5",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Inter",
    maxWidth: 120,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    "&:hover": {
      background: "#F5F5F5",
    },
  },

  // ── Status badge (Active / Discontinued) ──
  statusBadgeActive: {
    backgroundColor: "#DCFCE7",
    color: "#15803D",
    borderRadius: "999px",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "Inter",
    padding: "4px 10px",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  },
  statusBadgeDiscontinued: {
    backgroundColor: "#FEF3C7",
    color: "#92400E",
    borderRadius: "999px",
    fontSize: 12,
    fontWeight: 600,
    fontFamily: "Inter",
    padding: "4px 10px",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  },

  // ── Legend bar ──
  legendBar: {
    marginTop: 14,
    display: "flex",
    alignItems: "center",
    gap: 16,
    fontSize: 12,
    color: "#737373",
    flexWrap: "wrap",
  },
  legendDot: {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 6,
  },

  // ── Summary stats bar ──
  statsBar: {
    display: "flex",
    gap: 16,
    alignItems: "center",
    padding: "8px 14px",
    backgroundColor: "#F8F9FA",
    borderRadius: 8,
    border: "1px solid #E9ECEF",
    marginBottom: 16,
    flexWrap: "wrap",
  },
  statsDivider: {
    width: 1,
    height: 20,
    backgroundColor: "#DEE2E6",
  },

  // ── Medication cell text lines ──
  medName: {
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 600,
    color: "#171717",
    lineHeight: 1.4,
  },
  medStrength: {
    fontFamily: "Inter",
    fontSize: 12,
    fontWeight: 500,
    color: "#737373",
    lineHeight: 1.4,
    marginTop: 2,
  },
  medSubtitle: {
    fontFamily: "Inter",
    fontSize: 12,
    color: "#737373",
    lineHeight: 1.4,
    marginTop: 4,
  },
  chipNoCount: {
    background: "#F2F2F2",
    color: "#525252",
    border: "1px solid #E5E5E5",
  },

  notFoundText: {
    color: "#A8A8A8",
    fontFamily: "Inter",
    fontSize: "22px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "normal",
    [theme.fn.smallerThan("sm")]: {
      fontSize: "16px",
    },
  },
}));

export default useStyles;
